import { credentials, Metadata } from '@grpc/grpc-js'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-grpc'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  NodeTracerProvider,
} from '@opentelemetry/sdk-trace-node'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'
import { TRACER_NAME, TRACER_VERSION } from './constants'

type TracingManagerOptions = {
  telemetryEndpoint: string
  authToken: string
  tracerName: string
  tracerVersion: string
}

/** Manages OTEL tracer provider lifecycle and auth token updates for Frisk SDK. */
export class TracingManager {
  private telemetryEndpoint: string
  private tracerProvider: NodeTracerProvider
  private authToken: string
  private tracerName: string
  private tracerVersion: string

  public constructor({
    telemetryEndpoint,
    authToken,
    tracerName,
    tracerVersion,
  }: TracingManagerOptions) {
    this.telemetryEndpoint = telemetryEndpoint
    this.authToken = authToken
    this.tracerName = tracerName
    this.tracerVersion = tracerVersion
    this.tracerProvider = this.getTracerProvider()
  }

  public async shutdown(): Promise<void> {
    await this.tracerProvider.shutdown()
  }

  public getTracer() {
    return this.tracerProvider.getTracer(this.tracerName, this.tracerVersion)
  }

  public updateAuthToken(token: string) {
    this.authToken = token
  }

  private getTracerProvider() {
    // For gRPC, we don't need the /v1/traces path - that's only for HTTP
    const url = this.telemetryEndpoint.replace(/\/v1\/traces$/, '')

    // For secure connections, use CallCredentials for dynamic token injection
    // For insecure connections, use DynamicAuthMetadata which injects token on clone()
    const exporterConfig: ConstructorParameters<typeof OTLPTraceExporter>[0] = {
      url,
      credentials: credentials.combineChannelCredentials(
        credentials.createSsl(),
        credentials.createFromMetadataGenerator((_params, callback) => {
          const metadata = new Metadata()
          metadata.set('authorization', `Bearer ${this.authToken}`)
          callback(null, metadata)
        }),
      ),
    }

    const traceExporter = new OTLPTraceExporter(exporterConfig)

    const provider = new NodeTracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: TRACER_NAME,
        [ATTR_SERVICE_VERSION]: TRACER_VERSION,
      }),
      spanProcessors: [new BatchSpanProcessor(traceExporter, {})],
    })
    return provider
  }
}
